import { useMemo, useContext, createContext } from "react";
import { getAuthHeadersFromCookie } from "../../../utils/generalUtils";

export type AuthHeader = {
  Authorization?: string;
};

export type Origins = {
  get: string;
  www: string;
  auth: string;
  api: string;
  graphql: string;
  thomasnetXApi?: string;
  thomasnetHome: string;
  thomasnetPublicGql: string;
  thomasnetAuthGql: string;
  partFilesService: string;
  workcenter: string;
  metalsHome: string;
};

export type ConfigContextData = {
  isThomasnet: boolean;
  isHeadless: boolean;
  baseDomain: string;
  authHeaders: AuthHeader;
  origins: Origins;
  mockGraphqlServer: boolean;
  mockStreamChat: boolean;
  salesEmail: string;
  splitIOApiKey: string;
  streamApiKey: string;
  renderPaymentsHeader: boolean;
  publicUrl: string;
  partnerId: string;
  service?: string;
  routerLocation?: string;
  onBehalfOfUser?: {
    emailAddress: string;
    userId: string;
  };
  singleForm?: {
    id: string;
    onSubmit: (data: any) => Promise<boolean>;
    onClose: () => void;
  };
};

const originOrDefault = (
  origins: Partial<Origins>,
  key: keyof Origins,
  subdomain: string,
  baseDomain: string,
  path = ""
) => {
  const val = origins[key];

  if (typeof val !== "undefined" && val !== "") {
    return val;
  }

  return `https://${subdomain}.${baseDomain}${path}`;
};

const defaultConfig: ConfigContextData = {
  baseDomain: "stage.xometry.net",
  authHeaders: getAuthHeadersFromCookie(),
  publicUrl: "",
  mockGraphqlServer: false,
  isThomasnet: /thomasnet.com/.test(window.location.host),
  origins: {
    get: "https://get.stage.xometry.net/",
    www: "https://www.stage.xometry.net/",
    auth: "https://auth.stage.xometry.net/",
    api: "https://api.stage.xometry.net/",
    graphql: "https://www.stage.xometry.net/api/graphql/",
    thomasnetXApi: "https://stage-xapi.thomasnet.com",
    thomasnetHome: "https://test-www.thomasnet.com",
    thomasnetPublicGql: "https://test-graphql.thomasnet.com/public",
    thomasnetAuthGql: "https://test-graphql.thomasnet.com/graphql",
    partFilesService: "https://us-files.stage.xometry.net/",
    workcenter: "https://start.workcenter.stage.xometry.net",
    metalsHome: "https://stage-metal.thomasnet.com",
  },
  salesEmail: "seller.marketplace@xometry.com",
  splitIOApiKey: "",
  mockStreamChat: false,
  streamApiKey: "",
  renderPaymentsHeader: true,
  partnerId: "",
  isHeadless: true,
  service: "marketplace-frontend",
  routerLocation: undefined,
  singleForm: {
    id: "",
    onSubmit: async () => false,
    onClose: () => console.log("default close function"),
  },
};

export const ConfigContext = createContext<ConfigContextData>(defaultConfig);

export const ConfigProvider: React.FC<{ input: ConfigContextData }> = ({
  children,
  input,
}): React.ReactElement => {
  return useMemo<React.ReactElement>(() => {
    const {
      baseDomain,
      authHeaders,
      origins: partialOrigins,
      publicUrl,
      renderPaymentsHeader,
      salesEmail,
      mockGraphqlServer,
      mockStreamChat,
      splitIOApiKey,
      streamApiKey,
      partnerId,
      isThomasnet,
      isHeadless,
      service,
      routerLocation,
      onBehalfOfUser,
      singleForm,
    } = { ...defaultConfig, ...input };

    const origins: Origins = {
      get: originOrDefault(partialOrigins, "get", "get", baseDomain),
      www: originOrDefault(partialOrigins, "www", "www", baseDomain),
      auth: originOrDefault(partialOrigins, "auth", "auth", baseDomain),
      api: originOrDefault(partialOrigins, "api", "api", baseDomain),
      graphql: originOrDefault(
        partialOrigins,
        "graphql",
        "www",
        baseDomain,
        "/api/graphql/"
      ),
      thomasnetXApi: partialOrigins.thomasnetXApi,
      thomasnetHome: partialOrigins.thomasnetHome,
      thomasnetPublicGql: partialOrigins.thomasnetPublicGql,
      thomasnetAuthGql: partialOrigins.thomasnetAuthGql,
      partFilesService: originOrDefault(
        partialOrigins,
        "partFilesService",
        "us-files",
        baseDomain
      ),
      workcenter: originOrDefault(
        partialOrigins,
        "workcenter",
        "start.workcenter",
        baseDomain
      ),
      metalsHome: partialOrigins.metalsHome,
    };
    return (
      <ConfigContext.Provider
        value={{
          baseDomain,
          authHeaders,
          publicUrl: publicUrl.replace(/\/$/, ""),
          origins,
          renderPaymentsHeader,
          salesEmail,
          mockGraphqlServer,
          mockStreamChat,
          splitIOApiKey,
          streamApiKey,
          partnerId,
          isThomasnet,
          isHeadless,
          service,
          routerLocation,
          onBehalfOfUser,
          singleForm,
        }}
      >
        {children}
      </ConfigContext.Provider>
    );
  }, [JSON.stringify(input)]);
};

export function useConfig() {
  return useContext(ConfigContext);
}
